/**
 * Phone-number helpers used by ContactDetailsForm (account-level phone)
 * and the CheckoutPage shipping form (per-purchase recipient phone).
 *
 * Lenient by design: accepts foreign numbers so Swedish/Danish buyers
 * with Norwegian delivery addresses still get through. The Norway-only
 * normalisation step lives behind a separate check — bare 8-digit
 * mobile inputs are assumed Norwegian and prefixed with +47, anything
 * else passes through as long as it carries a country code.
 */

const STRIP_RE = /[\s\-().]/g;

// Allowed character classes after stripping noise: leading `+` plus
// digits, or just digits.
const ALLOWED_RE = /^\+?\d+$/;

// Sharetribe / Bring accept E.164 (max 15 digits incl. country code,
// min 6 to weed out typos like "1234").
const MIN_DIGITS = 6;
const MAX_DIGITS = 15;

const stripFormatting = value => (value || '').toString().replace(STRIP_RE, '');

/**
 * Returns `true` if the value looks like a usable phone number.
 *
 * Empty / nullish is considered valid here so callers can compose with
 * a separate `required` validator and keep the messages clean.
 *
 * Norwegian numbers (no country prefix OR `+47`) must have exactly 8
 * national digits — that's what Posten/Bring's booking API expects
 * and what every Bring docs example uses. Foreign numbers stay lenient
 * (6–15 total digits) so a Swedish/Danish buyer with a Norwegian
 * delivery address can still complete checkout.
 */
// A real Norwegian 8-digit subscriber number: leading digit 2–9.
const NORWEGIAN_NATIONAL_RE = /^[2-9]\d{7}$/;

export const isValidPhoneNumber = value => {
  if (!value) {
    return true;
  }
  const stripped = stripFormatting(value);
  if (!ALLOWED_RE.test(stripped)) {
    return false;
  }
  const digitsOnly = stripped.replace(/^\+/, '');
  if (digitsOnly.length < MIN_DIGITS || digitsOnly.length > MAX_DIGITS) {
    return false;
  }

  // Norwegian: no `+` prefix, or `+47` prefix → national part must be a
  // real 8-digit subscriber number, i.e. 8 digits with a leading 2–9
  // (0/1 aren't valid leading digits). This matches the format Stripe
  // accepts, so a number that passes here always prefills onboarding.
  if (!stripped.startsWith('+')) {
    return NORWEGIAN_NATIONAL_RE.test(digitsOnly);
  }
  if (stripped.startsWith('+47')) {
    return NORWEGIAN_NATIONAL_RE.test(digitsOnly.slice(2));
  }

  // Foreign country code — keep lenient.
  return true;
};

/**
 * React Final Form validator: returns the error message when invalid,
 * `undefined` when valid (or empty — leave required-ness to the caller).
 */
export const phoneNumberFormat = message => value =>
  isValidPhoneNumber(value) ? undefined : message;

/**
 * Normalise a user-typed phone number to E.164 (`+47XXXXXXXX`).
 *
 * - Strips spaces, dashes, dots, parentheses.
 * - Drops a leading `00` international prefix in favour of `+`.
 * - Bare 8-digit numbers are treated as Norwegian and prefixed with `+47`.
 * - Everything else passes through as `+<digits>` if a country code is present.
 *
 * Returns the cleaned-up string, or `null` when the input was empty.
 */
export const normalizePhoneNumber = value => {
  if (!value) {
    return null;
  }
  let stripped = stripFormatting(value);
  if (!stripped) {
    return null;
  }

  if (stripped.startsWith('00')) {
    stripped = `+${stripped.slice(2)}`;
  }

  if (stripped.startsWith('+')) {
    return stripped;
  }

  // Bare digits — assume Norwegian mobile/landline when the length
  // matches Norway's national numbering plan.
  if (stripped.length === 8) {
    return `+47${stripped}`;
  }

  return `+${stripped}`;
};
